import type { IconName } from "@/lib/icons";

export interface ContributorTask {
  key: string;
  label: string;
  hint: string;
  href: string;
  icon: IconName;
  /** In the main grid and the header tabs; the others sit under "More". */
  primary: boolean;
}

// Same labels and hints as the mobile app's home grid (yobi app_fr/app_en.arb:
// recordAction, validateAudios, homeAction*), so both apps name a task alike.
const copy = {
  fr: {
    speak: ["Enregistrer", "Lire des phrases à voix haute"],
    verifyAudio: ["Valider les audios", "Écouter et approuver"],
    transcribe: ["Transcrire", "Écrire ce que vous entendez"],
    translate: ["Traduire", "Du français vers votre langue"],
    verifyText: ["Valider des textes", "Vérifier des transcriptions"],
    images: ["Images", "Reconnaître des caractères manuscrits"],
    answerAudio: ["Audio initial", "Répondre aux questions à l'oral"],
    answerText: ["Texte initial", "Répondre aux questions à l'écrit"],
    transcribeInitial: ["Transcription initiale", "Transcrire les réponses audio"],
    reviewInitial: ["Relecture initiale", "Relire les réponses et transcriptions"],
  },
  en: {
    speak: ["Record", "Read sentences aloud"],
    verifyAudio: ["Validate audios", "Listen and approve"],
    transcribe: ["Transcribe", "Type what you hear"],
    translate: ["Translate", "From French to your language"],
    verifyText: ["Validate texts", "Check transcriptions"],
    images: ["Images", "Recognise handwritten characters"],
    answerAudio: ["Initial audio", "Answer questions aloud"],
    answerText: ["Initial text", "Answer questions in writing"],
    transcribeInitial: ["Initial transcription", "Transcribe the audio answers"],
    reviewInitial: ["Initial review", "Review answers and transcriptions"],
  },
} as const;

/**
 * Every kind of contribution the app offers, once. The header, its "More"
 * menu, the phone task sheet and the home grid all read this list, so a task
 * can't be reachable from one and missing from another.
 *
 * `collector`: a collector answers the initial audio inside their own
 * workspace (spec 018), so that entry opens the collector section instead of
 * the standalone page.
 */
export function contributorTasks(lang: string, { collector = false }: { collector?: boolean } = {}): ContributorTask[] {
  const c = copy[lang === "en" ? "en" : "fr"];
  const base = `/${lang}`;
  const task = (key: keyof typeof c, href: string, icon: IconName, primary: boolean): ContributorTask => ({
    key,
    label: c[key][0],
    hint: c[key][1],
    href: `${base}${href}`,
    icon,
    primary,
  });
  // Mobile order: record, validate audio, transcribe, translate, validate
  // texts, then the less frequent ones.
  return [
    task("speak", "/speak-contribute", "mic", true),
    task("verifyAudio", "/validate-audio", "volume2", true),
    task("transcribe", "/transcription", "edit", true),
    task("translate", "/transcription-phrase", "languages", true),
    task("verifyText", "/validate-transcription", "checkCircle", true),
    task("images", "/label-images", "image", false),
    task("answerAudio", collector ? "/collector?section=initial-audio" : "/initial-audio", "mic", false),
    task("answerText", "/initial-text", "fileText", false),
    task("transcribeInitial", "/initial-transcription", "languages", false),
    task("reviewInitial", "/initial-review", "checkCircle", false),
  ];
}
